import { Box, Input, Select, Textarea } from '@nerdwallet/currency';
import Accordion from '@nerdwallet/react-accordion';
import { Fragment, useMemo } from 'react';
import BaseLottieAnimation from '~/app/components/lottie-animation/base-lottie-animation';
import { animationModes } from '~/app/components/lottie-animation/lib';
import { useMediaQuery } from '~/app/lib/use-media-query';
import { DynamicLottieAnimationProps } from './types';
import { useDynamicLottieAnimation } from './use-dynamic-lottie-animation';

const lottiePlayerId = 'lottie-player';

const ANIMATION_MODES = [
  { label: 'On Scroll', value: animationModes.SEEK },
  { label: 'When Visible', value: animationModes.PLAY },
];

export const DynamicLottieAnimation = ({
  animationData,
  animationMode = animationModes.SEEK,
  dimensions = {
    width: 300,
    height: 300,
  },
  visibility = {
    x: '0.5',
    y: '1.0',
  },
  frames = {
    x: '0.5',
    y: '1.0',
  },
}: DynamicLottieAnimationProps) => {
  const size = useMediaQuery();
  // Every time "dimensionsPerViewport' changes we'll re-map dimensions to match the viewport.
  // If viewport is not provided we'll used the last one
  const dimensionsPerViewport = useMemo(() => {
    return {
      lg: { width: dimensions.width, height: dimensions.height },
      md: {
        width: dimensions?.tablet?.width || dimensions.width,
        height: dimensions?.tablet?.height || dimensions.height,
      },
      sm: {
        width:
          dimensions?.mobile?.width ||
          dimensions?.tablet?.width ||
          dimensions.width,
        height:
          dimensions?.mobile?.height ||
          dimensions?.tablet?.height ||
          dimensions.height,
      },
    }[size];
  }, [dimensions, size]);

  return (
    <BaseLottieAnimation
      lottiePlayerId={lottiePlayerId}
      animationData={animationData}
      animationMode={animationMode}
      visibility={visibility}
      frames={frames}
      dimensions={dimensionsPerViewport}
    />
  );
};

export const DynamicLottieAnimationForm = ({
  fieldPath = '',
}: {
  fieldPath?: string;
}) => {
  const {
    animationData,
    animationMode,
    visibility,
    frames,
    dimensions,
    handleAnimationDataChange,
    handleAnimationModeChange,
    handleVisibilityXChange,
    handleVisibilityYChange,
    handleFramesXChange,
    handleFramesYChange,
    handleWidthChange,
    handleHeightChange,
    handleTabletWidthChange,
    handleTabletHeightChange,
    handleMobileWidthChange,
    handleMobileHeightChange,
  } = useDynamicLottieAnimation({
    fieldPath,
  });

  return (
    <Box paddingY={2}>
      <Box paddingY={2}>
        <Textarea
          label="Animation Data"
          value={animationData}
          onChange={handleAnimationDataChange}
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Select
          label="Animation Mode"
          value={animationMode}
          onChange={handleAnimationModeChange}
          options={ANIMATION_MODES}
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Input
          label="Visibility X"
          value={visibility.x}
          onChange={handleVisibilityXChange}
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Input
          label="Visibility Y"
          value={visibility.y}
          onChange={handleVisibilityYChange}
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Input
          label="Frames X"
          value={frames.x}
          onChange={handleFramesXChange}
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Input
          label="Frames Y"
          value={frames.y}
          onChange={handleFramesYChange}
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Input
          value={dimensions.width}
          onChange={handleWidthChange}
          label="Width"
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Input
          value={dimensions.height}
          onChange={handleHeightChange}
          label="Height"
          fullWidth
        />
      </Box>
      <Box paddingY={2}>
        <Accordion
          items={[
            {
              id: 'tablet',
              title: 'Tablet',
              children: (
                <Fragment>
                  <Box paddingY={2}>
                    <Input
                      label="Width"
                      value={dimensions?.tablet?.width}
                      onChange={handleTabletWidthChange}
                      fullWidth
                    />
                  </Box>
                  <Box paddingY={2}>
                    <Input
                      label="Height"
                      value={dimensions?.tablet?.height}
                      onChange={handleTabletHeightChange}
                      fullWidth
                    />
                  </Box>
                </Fragment>
              ),
            },
            {
              id: 'mobile',
              title: 'Mobile',
              children: (
                <Fragment>
                  <Box paddingY={2}>
                    <Input
                      label="Width"
                      value={dimensions?.mobile?.width}
                      onChange={handleMobileWidthChange}
                      fullWidth
                    />
                  </Box>
                  <Box paddingY={2}>
                    <Input
                      label="Height"
                      value={dimensions?.mobile?.height}
                      onChange={handleMobileHeightChange}
                      fullWidth
                    />
                  </Box>
                </Fragment>
              ),
            },
          ]}
        />
      </Box>
    </Box>
  );
};
